import React from "react";
import { cn } from "@/lib/utils";
import { User, Activity, FileText, CheckCircle, Users } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface EnrollmentProgressProps {
  currentStep: 'form' | 'activities' | 'summary' | 'success' | 'configuration';
  className?: string;
}

const steps = [
  { 
    key: 'form', 
    label: 'Datos del alumno/a', 
    mobileLabel: 'Datos',
    icon: User,
    description: 'Información personal',
    number: 1
  },
  { 
    key: 'activities', 
    label: 'Seleccionar actividades', 
    mobileLabel: 'Actividades',
    icon: Users,
    description: 'Elige las actividades',
    number: 2
  },
  { 
    key: 'summary', 
    label: 'Resumen', 
    mobileLabel: 'Resumen',
    icon: FileText,
    description: 'Revisa la información',
    number: 3
  },
  { 
    key: 'success', 
    label: 'Confirmación', 
    mobileLabel: 'Listo',
    icon: CheckCircle,
    description: 'Inscripción completada',
    number: 4
  },
];

export function EnrollmentProgress({ currentStep, className }: EnrollmentProgressProps) {
  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  const isMobile = useIsMobile();

  // Mobile component
  if (isMobile) {
    return (
      <div className={cn("w-full py-4 px-4", className)}>
        <div className="max-w-sm mx-auto">
          {/* Mobile progress dots */}
          <div className="flex justify-center items-center space-x-2 mb-4">
            {steps.map((_, index) => {
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;

              return (
                <div
                  key={index}
                  className={cn(
                    "transition-all duration-300",
                    {
                      "w-8 h-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full": isActive,
                      "w-2 h-2 bg-green-500 rounded-full": isCompleted,
                      "w-2 h-2 bg-gray-300 rounded-full": index > currentStepIndex,
                    }
                  )}
                />
              );
            })}
          </div>

          {/* Current step info */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div
                className={cn(
                  "w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300",
                  "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                )}
              >
                {currentStepIndex < steps.length - 1 || currentStep !== 'success' ? (
                  React.createElement(steps[currentStepIndex]?.icon || User, { 
                    className: "w-8 h-8" 
                  })
                ) : (
                  <CheckCircle className="w-8 h-8" />
                )}
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {steps[currentStepIndex]?.mobileLabel || steps[currentStepIndex]?.label}
            </h3>
            
            <p className="text-sm text-gray-600 mb-3">
              {steps[currentStepIndex]?.description}
            </p>

            <div className="inline-flex items-center px-3 py-1 bg-blue-50 rounded-full border border-blue-200">
              <span className="text-xs font-medium text-blue-700">
                Paso {currentStepIndex + 1} de {steps.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop component (original design, slightly improved)
  return (
    <div className={cn("w-full py-8 px-4", className)}>
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          {/* Progress bar background */}
          <div className="absolute top-6 left-0 w-full h-1 bg-gray-200 rounded-full" />
          
          {/* Progress bar fill */}
          <div 
            className="absolute top-6 left-0 h-1 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 rounded-full transition-all duration-700 ease-in-out"
            style={{ 
              width: `${(currentStepIndex / (steps.length - 1)) * 100}%` 
            }}
          />

          <div className="relative flex justify-between">
            {steps.map((step, index) => {
              const isActive = index === currentStepIndex;
              const isCompleted = index < currentStepIndex;
              const isUpcoming = index > currentStepIndex;
              const Icon = step.icon;

              return (
                <div key={step.key} className="flex flex-col items-center group">
                  {/* Step circle */}
                  <div
                    className={cn(
                      "relative w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all duration-300 transform hover:scale-105",
                      {
                        "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30 ring-4 ring-blue-100": isActive,
                        "bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30": isCompleted,
                        "bg-white border-2 border-gray-300 text-gray-400 shadow-sm": isUpcoming,
                      }
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6" />
                    ) : (
                      <Icon className={cn("w-6 h-6", {
                        "animate-pulse": isActive
                      })} />
                    )}
                    
                    {/* Pulse animation for active step */}
                    {isActive && (
                      <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                    )}
                  </div>

                  {/* Step label and description */}
                  <div className="mt-4 text-center max-w-32">
                    <div
                      className={cn(
                        "text-sm font-medium transition-colors duration-200",
                        {
                          "text-blue-600": isActive,
                          "text-green-600": isCompleted,
                          "text-gray-500": isUpcoming,
                        }
                      )}
                    >
                      {step.label}
                    </div>
                    <div
                      className={cn(
                        "text-xs mt-1 transition-colors duration-200",
                        {
                          "text-blue-500": isActive,
                          "text-green-500": isCompleted,
                          "text-gray-400": isUpcoming,
                        }
                      )}
                    >
                      {step.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current step indicator */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full border border-blue-200">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-3" />
            <span className="text-sm font-medium text-blue-700">
              Paso {currentStepIndex + 1} de {steps.length}: {steps[currentStepIndex]?.label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}